import React, { createContext, useContext, useState } from "react";
import type { CallBackProps } from "react-joyride";

interface TutorialState {
  isTutorialRunning: boolean;
  tutorialStepIndex: number;
  routeTutorials: string[];
  activeTutorialId: string | null;
  completedTutorials: Set<string>;
}

export interface TutorialContextValue extends TutorialState {
  effectiveRouteTutorials: string[];
  setActiveTutorialId: (tutorialId: string | null) => void;
  startTutorial: (tutorialId?: string) => void;
  closeTutorial: (tutorialId?: string) => void;
  resetTutorial: (tutorialId?: string) => void;
  resetAllTutorials: () => void;
  isTutorialCompleted: (tutorialId: string) => boolean;
  handleTutorialCallback: (data: CallBackProps) => void;
  addRouteTutorialIds: (ids: string[]) => void;
  removeRouteTutorialIds: (ids: string[]) => void;
}

export const TutorialContext = createContext<TutorialContextValue | undefined>(
  undefined,
);

// Base storage key - individual tutorials will append their ID
const TUTORIAL_STORAGE_KEY_BASE = "atlas-tutorial-completed";

export function TutorialProvider({ children }: { children: React.ReactNode }) {
  const [tutorialState, setTutorialState] = useState<TutorialState>(() => {
    // Load completed tutorials from localStorage
    const completedTutorials = new Set<string>();

    // Load individual tutorial completion status
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TUTORIAL_STORAGE_KEY_BASE)) {
        const tutorialId = key.replace(TUTORIAL_STORAGE_KEY_BASE + "-", "");
        if (localStorage.getItem(key) === "true") {
          completedTutorials.add(tutorialId);
        }
      }
    });

    return {
      isTutorialRunning: false,
      tutorialStepIndex: 0,
      routeTutorials: [],
      activeTutorialId: null,
      completedTutorials,
    };
  });

  const startTutorial = () => {
    setTutorialState((prev) => ({
      ...prev,
      isTutorialRunning: true,
      tutorialStepIndex: 0,
      activeTutorialId: null,
    }));
  };

  const closeTutorial = (tutorialId?: string) => {
    setTutorialState((prev) => {
      const newCompletedTutorials = new Set(prev.completedTutorials);
      if (tutorialId) {
        newCompletedTutorials.add(tutorialId);
        // Save individual tutorial completion
        localStorage.setItem(
          `${TUTORIAL_STORAGE_KEY_BASE}-${tutorialId}`,
          "true",
        );
      }

      return {
        ...prev,
        isTutorialRunning: false,
        tutorialStepIndex: 0,
        activeTutorialId: null,
        completedTutorials: newCompletedTutorials,
      };
    });
  };

  const resetTutorial = (tutorialId?: string) => {
    if (tutorialId) {
      // Reset specific tutorial
      localStorage.removeItem(`${TUTORIAL_STORAGE_KEY_BASE}-${tutorialId}`);
      setTutorialState((prev) => {
        const newCompletedTutorials = new Set(prev.completedTutorials);
        newCompletedTutorials.delete(tutorialId);
        return {
          ...prev,
          completedTutorials: newCompletedTutorials,
        };
      });
    } else {
      // Reset all tutorials (legacy behavior)
      resetAllTutorials();
    }
  };

  const resetAllTutorials = () => {
    // Remove all tutorial-related localStorage items
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TUTORIAL_STORAGE_KEY_BASE)) {
        localStorage.removeItem(key);
      }
    });

    setTutorialState((prev) => ({
      ...prev,
      isTutorialRunning: false,
      tutorialStepIndex: 0,
      activeTutorialId: null,
      completedTutorials: new Set(),
    }));
  };

  const isTutorialCompleted = (tutorialId: string) => {
    return tutorialState.completedTutorials.has(tutorialId);
  };

  const handleTutorialCallback = (data: CallBackProps) => {
    const { action, index, type } = data;

    // Update step index
    if (type === "step:after" && action === "next") {
      setTutorialState((prev) => ({
        ...prev,
        tutorialStepIndex: index + 1,
      }));
    } else if (type === "step:after" && action === "prev") {
      setTutorialState((prev) => ({
        ...prev,
        tutorialStepIndex: Math.max(0, index - 1),
      }));
    }
  };

  const addRouteTutorialIds = (ids: string[]) => {
    setTutorialState((prev) => ({
      ...prev,
      routeTutorials: [...new Set([...prev.routeTutorials, ...ids])],
    }));
  };

  const removeRouteTutorialIds = (ids: string[]) => {
    setTutorialState((prev) => ({
      ...prev,
      routeTutorials: prev.routeTutorials.filter((id) => !ids.includes(id)),
    }));
  };

  const setActiveTutorialId = (tutorialId: string | null) => {
    setTutorialState((prev) => ({
      ...prev,
      activeTutorialId: tutorialId,
    }));
  };

  const value: TutorialContextValue = {
    isTutorialRunning: tutorialState.isTutorialRunning,
    tutorialStepIndex: tutorialState.tutorialStepIndex,
    completedTutorials: tutorialState.completedTutorials,
    routeTutorials: tutorialState.routeTutorials,
    activeTutorialId: tutorialState.activeTutorialId,
    effectiveRouteTutorials: tutorialState.routeTutorials,
    setActiveTutorialId,
    addRouteTutorialIds,
    removeRouteTutorialIds,
    startTutorial,
    closeTutorial,
    resetTutorial,
    resetAllTutorials,
    isTutorialCompleted,
    handleTutorialCallback,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error("useTutorial must be used within a TutorialProvider");
  }
  return context;
}
